import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NominalAccount } from '@/types/financials';

// Maximum length for nominal account codes (matches backend MaxLength(50))
const MAX_NOMINAL_CODE_LENGTH = 50;

interface NominalPickerProps {
  readonly value?: string;
  readonly displayValue?: string;
  readonly onValueChange: (code: string, name: string) => void;
  readonly nominalAccounts: NominalAccount[];
  readonly loading?: boolean;
  readonly error?: boolean;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly className?: string;
}

export function NominalPicker({
  value,
  displayValue,
  onValueChange,
  nominalAccounts,
  loading = false,
  error = false,
  disabled = false,
  placeholder = 'Select or enter nominal account...',
  className,
}: NominalPickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');

  // Sync input value with external value changes
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const hasIntegrationData = nominalAccounts.length > 0;

  const handleSelect = (account: NominalAccount | null) => {
    if (account === null) {
      onValueChange('', '');
      setInputValue('');
    } else {
      onValueChange(account.code, account.name);
      setInputValue(account.code);
    }
    setOpen(false);
  };

  const handleInputChange = (newValue: string) => {
    // Enforce max length
    const truncatedValue = newValue.slice(0, MAX_NOMINAL_CODE_LENGTH);
    setInputValue(truncatedValue);
    
    // Check if the value matches an existing account
    const matchingAccount = nominalAccounts.find(a => a.code === truncatedValue);
    if (matchingAccount) {
      onValueChange(matchingAccount.code, matchingAccount.name);
    } else {
      // Custom entry - use the value as both code and name
      onValueChange(truncatedValue, truncatedValue);
    }
  };

  const handleInputBlur = () => {
    // Ensure the value is committed on blur
    if (inputValue !== value) {
      const matchingAccount = nominalAccounts.find(a => a.code === inputValue);
      if (matchingAccount) {
        onValueChange(matchingAccount.code, matchingAccount.name);
      } else {
        onValueChange(inputValue, inputValue);
      }
    }
  };

  const getSelectedDisplay = () => {
    if (loading) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading...
        </span>
      );
    }
    if (value) {
      const selectedAccount = nominalAccounts.find(a => a.code === value);
      if (selectedAccount) return `${selectedAccount.code} - ${selectedAccount.name}`;
      if (displayValue && displayValue !== value) return `${value} - ${displayValue}`;
      return value;
    }
    return placeholder;
  };

  const getDisabledDisplayValue = (): string => {
    if (!value) return 'None';
    if (displayValue && displayValue !== value) return `${value} - ${displayValue}`;
    return value;
  };

  if (disabled) {
    return (
      <Input
        value={getDisabledDisplayValue()}
        readOnly
        className={cn('bg-muted', className)}
      />
    );
  }

  // If no integration data, show a simple input field for custom entry
  if (!hasIntegrationData && !loading) {
    return (
      <div className={className}>
        <Input
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          maxLength={MAX_NOMINAL_CODE_LENGTH}
          className="w-full"
        />
        {error && (
          <p className="text-sm text-destructive mt-1">Failed to load nominal accounts</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn("w-full justify-between font-normal bg-white", className)}
            disabled={loading}
          >
            {getSelectedDisplay()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search or enter custom value..." 
              value={inputValue}
              onValueChange={(search) => {
                setInputValue(search);
              }}
            />
            <CommandEmpty>
              {error && 'Failed to load nominal accounts.'}
              {!error && inputValue && (
                <CommandItem
                  onSelect={() => {
                    const truncatedValue = inputValue.slice(0, MAX_NOMINAL_CODE_LENGTH);
                    onValueChange(truncatedValue, truncatedValue);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  Use &quot;{inputValue.slice(0, MAX_NOMINAL_CODE_LENGTH)}&quot;
                </CommandItem>
              )}
              {!error && !inputValue && 'Type to enter a custom value'}
            </CommandEmpty>
            <CommandGroup className="max-h-64 overflow-y-auto">
              <CommandItem
                key="none"
                value="none"
                onSelect={() => handleSelect(null)}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !value ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="text-muted-foreground">None</span>
              </CommandItem>
              {nominalAccounts.map((account) => (
                <CommandItem
                  key={account.id}
                  value={`${account.code} ${account.name}`}
                  onSelect={() => handleSelect(account)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === account.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="font-medium">{account.code}</span>
                  <span className="ml-2 text-muted-foreground">{account.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive mt-1">Failed to load nominal accounts</p>
      )}
    </div>
  );
}
